/**
 * PropelPay Basket Item Eligibility widget integration
 *
 * Source of truth:
 * - Variant ID --> Product JSON --> Variant data
 * - ShopifyAnalytics --> currency & fallback variant ID
 *
 * Pricing:
 * - variant.price = current selling price (EX VAT / net) in minor units
 * - VAT is calculated from the net price (integer math to mirror Shopify rounding)
 * - We send unitNetPrice and unitVatAmount separately to match PropelPay
 *
 * VAT (UK 20%):
 * - PropelPay expects:
 * 	- unitNetPrice = EX-VAT (net) in minor units
 * 	- unitVatAmount = VAT amount in minor units
 */

// Fetch using dev app when working locally.
const DEV_MODE = false;

const widgetContainer = document.querySelector("#basketItemIndicativeWidget");

// UK VAT defaults.
const VAT_RATE = 0.2;

let subpath = "proxy";
if (DEV_MODE) subpath = "proxy-dev";
const BASKET_ENDPOINT = `/apps/${subpath}/basket-item-eligibility/`;

// Cache product JSON so it is only fetched once.
let productJsonPromise = null;

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */
function getVariantIdFromUrlOrForm() {
	// URL param ?variant=123
	const searchParams = new URLSearchParams(window.location.search);
	const urlVariantId = searchParams.get("variant");
	if (urlVariantId) return String(urlVariantId);

	// Product form input name="id"
	const formInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
	return formInput && formInput.value ? String(formInput.value) : null;
}

function waitForAnalyticsMeta(options) {
	const { timeoutMs = 5000, intervalMs = 50 } = options || {};
	const startTime = Date.now();

	return new Promise((resolve) => {
		function poll() {
			const analytics = window.ShopifyAnalytics;
			const meta = analytics && analytics.meta;

			if (meta && (meta.currency || meta.selectedVariantId)) {
				resolve(meta);
				return;
			}

			if (Date.now() - startTime > timeoutMs) {
				resolve(meta || null);
				return;
			}

			setTimeout(poll, intervalMs);
		}

		poll();
	});
}

/* -------------------------------------------------------------------------- */
/* Product / Variant helpers                                                  */
/* -------------------------------------------------------------------------- */
function getProductJson() {
	// Extract product handle from /products/<handle>
	const match = window.location.pathname.match(/^\/products\/([^\/]+)/);
	if (!match || !match[1]) {
		return Promise.reject(new Error("Not on a Shopify product page"));
	}

	const productHandle = match[1];

	return fetch(`/products/${productHandle}.js`, { credentials: "same-origin" }).then((response) => {
		if (!response.ok) {
			throw new Error(`Failed to load product JSON (${response.status})`);
		}
		return response.json();
	});
}

function getVariantById(variantId) {
	if (!productJsonPromise) {
		productJsonPromise = getProductJson();
	}

	return productJsonPromise.then((product) => {
		const variants = product && product.variants ? product.variants : [];

		const variant = variants.find((v) => String(v.id) === String(variantId));
		if (!variant) {
			throw new Error(`Variant ${variantId} not found in product JSON`);
		}

		return variant;
	});
}

/* -------------------------------------------------------------------------- */
/* Pricing helpers                                                            */
/* -------------------------------------------------------------------------- */
// VAT per UNIT, rounded UP to the nearest penny.
// Example: 91583 * 0.2 = 18316.6 -> 18317
function computeUnitVatFromNet(netMinorUnits) {
	const unitVatAmount = Math.ceil(netMinorUnits * VAT_RATE);

	return {
		unitNetPrice: netMinorUnits,
		unitVatAmount
	};
}

/* -------------------------------------------------------------------------- */
/* Payload builder                                                            */
/* -------------------------------------------------------------------------- */
function buildBasketPayload(variant, currency) {
	// variant.price is the current selling price.
	const unitNetPriceFromShopify = variant.price;

	const { unitNetPrice, unitVatAmount } = computeUnitVatFromNet(unitNetPriceFromShopify);

	return {
		currency,
		sku: variant.sku,
		unitNetPrice,
		unitVatAmount,
	};
}

/* -------------------------------------------------------------------------- */
/* Main render entry                                                          */
/* -------------------------------------------------------------------------- */
function renderWidgetFromVariantId(variantId) {
	if (!widgetContainer) return;

	return waitForAnalyticsMeta().then((analyticsMeta) => {
		const meta = analyticsMeta || {};

		const currency = meta.currency || (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.currency);

		return getVariantById(variantId).then((variant) => {
			const requestBody = buildBasketPayload(variant, currency);

			return fetch(BASKET_ENDPOINT, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(requestBody),
			})
					.then(async (response) => {
						if (!response.ok) {
							const text = await response.text();
							throw new Error(`Request failed (${response.status}): ${text}`);
						}
						return response.json();
					})
					.then((data) => {
						console.log("PropelPay eligibility response:", data);

						if (data.status === "eligible") {
							widgetContainer.innerHTML = `<propel-pay-basket-item-indicative-pricing token="${data.journeyToken}"></propel-pay-basket-item-indicative-pricing>`;
						} else {
							widgetContainer.innerHTML = "";
						}
					})
					.catch(() => {
						widgetContainer.innerHTML = "";
					});
		});
	});
}

/* -------------------------------------------------------------------------- */
/* Init on page load                                                          */
/* -------------------------------------------------------------------------- */
(function initPropelPayWidget() {
	const initialVariantId = getVariantIdFromUrlOrForm();

	if (initialVariantId) {
		renderWidgetFromVariantId(initialVariantId).catch((err) => {
			console.error("PropelPay init error:", err);
		});
		return;
	}

	waitForAnalyticsMeta().then((meta) => {
		if (meta && meta.selectedVariantId) {
			renderWidgetFromVariantId(String(meta.selectedVariantId)).catch((err) => {
				console.error("PropelPay init error:", err);
			});
		} else {
			console.warn("PropelPay: no initial variant id found on load");
		}
	});
})();

/* -------------------------------------------------------------------------- */
/* Variant change subscription                                                */
/* -------------------------------------------------------------------------- */
subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
	try {
		const variant = event && event.data && event.data.variant ? event.data.variant : null;
		if (!variant || !variant.id) return;

		console.log("PUB_SUB variantChange:", variant);
		renderWidgetFromVariantId(String(variant.id)).catch((err) => {
			console.error("PropelPay variant change error:", err);
		});
	} catch (err) {
		console.error("PropelPay variant change error:", err);
	}
});
